
const linearPalette = {
  dark: {
    background: {
      default: '#0B1020', // deep cold navy / blue-black (app bg, level 1)
      paper: '#141B2E',    // blue-tinted card surface (level 3), crisp against bg
    },
    text: {
      primary: '#EAEEF6', // near-white, cool
      secondary: '#94A0B8', // cool blue-gray
    },
    primary: {
      main: '#5E6AD2', // brand indigo (unchanged)
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#94A0B8',
    },
    divider: 'rgba(255,255,255,0.08)', // subtle cold border, crisp not heavy
    // `light` = the tinted SURFACE behind a semantic state (badge fill, highlighted
    // row). In dark mode it must stay dark, otherwise a pastel block sits under light
    // body text and the row becomes unreadable. Translucent tints of the same hue so
    // they layer correctly over both the app bg and the raised card.
    success: {
      main: '#32D583', // Success 500 — lifted for contrast on navy
      light: 'rgba(18, 183, 106, 0.18)',
    },
    warning: {
      main: '#FDB022', // Warning 500
      light: 'rgba(247, 144, 9, 0.18)',
    },
    error: {
      main: '#F97066', // Error 500
      light: 'rgba(240, 68, 56, 0.18)',
    },
    info: {
      main: '#53B1FD', // Blue 500
      light: 'rgba(21, 112, 239, 0.22)',
    },
  },
  light: {
    background: {
      default: '#FAFAFC',
      paper: '#FFFFFF',
    },
    text: {
      // Secondary is not only body text on the card: it labels the table header band
      // (--color-bg-subtle #F6F6F9), fills the neutral "Inactive" badge
      // (--color-gray-200 #F2F4F7) and carries the count inside the active Leads scope
      // segment (the brand indigo at 14%, the darkest of the three). #71717A cleared
      // 4.83 on white but 4.48 / 4.39 / 4.06 on those, so every table header on every
      // list screen sat under AA. Tuned against the darkest; still 5.52 on white.
      primary: '#18181B',
      secondary: '#686870',
    },
    primary: {
      main: '#5E6AD2',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#71717A',
    },
    divider: '#EFF0F7',
    success: {
      main: '#12B76A', // Success 600
      light: '#D1FADF', // Success 200
    },
    warning: {
      main: '#F79009', // Warning 600
      light: '#FEF0C7', // Warning 200
    },
    error: {
      main: '#F04438', // Error 600
      light: '#FEE4E2', // Error 200
    },
    info: {
      main: '#1570EF', // Blue 700
      light: '#D1E9FF', // Blue 200
    },
  },
};

export const getDesignTokens = (mode) => ({
  palette: {
    mode,
    ...linearPalette[mode],
  },
  typography: {
    fontFamily: ['Inter', 'system-ui', 'sans-serif'].join(','),
    h1: { fontWeight: 600 },
    h2: { fontWeight: 600 },
    h3: { fontWeight: 600 },
    h4: { fontWeight: 600 },
    h5: { fontWeight: 600 },
    h6: { fontWeight: 600 },
    button: {
      textTransform: 'none',
      fontWeight: 500,
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          boxShadow: 'none',
          '&:hover': {
            boxShadow: 'none',
          },
        },
        containedPrimary: {
          // contained = white text ON the brand colour, so the FILL value is correct here
          '&:hover': {
            backgroundColor: '#4B55AA', // Slightly darker blurple
          },
        },
        // Outlined and text buttons render the brand colour AS TEXT on a surface, where
        // the fill value is not legible: "Select by processor" and "Select all clean" on
        // Daily Processing measured 3.64:1 in dark. index.css only reaches Tailwind's
        // .text-primary-main, not MUI's emotion styles, so they need routing here too.
        outlinedPrimary: {
          color: 'var(--color-primary-text)',
          borderColor: 'var(--color-primary-text)',
        },
        textPrimary: {
          color: 'var(--color-primary-text)',
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        // Tab labels are the same case: "To Process" and "Export" measured 4.03:1.
        root: {
          '&.Mui-selected': {
            color: 'var(--color-primary-text)',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundImage: 'none', // Remove MUI default gradient overlay in dark mode
          ...(theme.palette.mode === 'dark' && {
            border: `1px solid ${theme.palette.divider}`,
            boxShadow: 'none',
          }),
        }),
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        // Left alone, colorDefault is MUI's own grey (#BDBDBD light, #757575 dark) with
        // a near-white contrastText — it never reads the palette. The assignee initials
        // on Daily Processing measured 1.80:1 in light and 4.11:1 in dark. Both themes
        // get a neutral from the palette and the body text colour on top.
        colorDefault: {
          backgroundColor: mode === 'dark' ? '#2A3352' : '#E4E7EC',
          color: mode === 'dark' ? '#EAEEF6' : '#18181B',
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: ({ theme }) => ({
          backgroundColor: theme.palette.mode === 'dark' ? '#1C2340' : '#1D2939',
          color: '#FFFFFF',
          fontSize: 12,
          fontWeight: 500,
          lineHeight: 1.45,
          padding: '8px 10px',
          borderRadius: 8,
          maxWidth: 240,
          boxShadow: '0 8px 24px -6px rgba(16,24,40,0.35)',
          border: theme.palette.mode === 'dark' ? '1px solid rgba(255,255,255,0.10)' : 'none',
        }),
        arrow: ({ theme }) => ({
          color: theme.palette.mode === 'dark' ? '#1C2340' : '#1D2939',
        }),
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: theme.palette.mode === 'dark' ? 'rgba(28, 28, 30, 0.8)' : 'rgba(255, 255, 255, 0.8)',
          backdropFilter: 'blur(12px)',
          borderBottom: `1px solid ${theme.palette.divider}`,
          boxShadow: 'none',
          color: theme.palette.text.primary,
        }),
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 6,
          },
        },
      },
    },
    // Global outlined-input styling so every input / select / date-picker across the
    // app uses theme-aware borders (fixes dark-mode borders systemwide).
    MuiOutlinedInput: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: 8,
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.divider,
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.16)' : '#D0D5DD',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.primary.main,
            borderWidth: 1,
          },
        }),
      },
    },
    MuiDataGrid: {
      styleOverrides: {
        root: ({ theme }) => ({
          border: 'none',
          '& .MuiDataGrid-columnHeaders': {
            // Same light grey title band as the hand-rolled tables.
            backgroundColor: 'var(--color-bg-subtle)',
            borderBottom: `1px solid ${theme.palette.divider}`,
            color: theme.palette.text.secondary,
            fontSize: '13px',
            textTransform: 'uppercase',
            fontWeight: 600,
          },
          '& .MuiDataGrid-cell': {
            borderBottom: `1px solid ${theme.palette.divider}`,
            color: theme.palette.text.primary,
          },
          '& .MuiDataGrid-row:hover': {
            backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.04)' : 'rgba(0, 0, 0, 0.04)',
          },
          '& .MuiDataGrid-footerContainer': {
            borderTop: `1px solid ${theme.palette.divider}`,
          },
          '& .MuiCheckbox-root': {
            color: theme.palette.text.secondary,
          },
        }),
      },
    },
    MuiCssBaseline: {
      styleOverrides: `
        :root {
          --color-bg-default: ${linearPalette[mode].background.default};
          --color-bg-paper: ${linearPalette[mode].background.paper};
          --color-bg-elevated: ${mode === 'dark' ? '#1C2340' : '#FFFFFF'};
          /* Table header band. Solid rather than translucent so it does not
             stack differently depending on what sits behind the card. */
          --color-bg-subtle: ${mode === 'dark' ? '#1A2338' : '#F6F6F9'};
          --color-border-strong: ${mode === 'dark' ? 'rgba(255,255,255,0.14)' : '#E4E7EC'};
          --color-text-primary: ${linearPalette[mode].text.primary};
          --color-text-secondary: ${linearPalette[mode].text.secondary};
          --color-primary-main: ${linearPalette[mode].primary.main};
          /* Brand indigo used as TEXT (links, active states). Same one-token-two-roles
             problem as the semantics: #5E6AD2 fills buttons/sidebar (white text on it)
             AND is used as text on surfaces. On the dark card it reads 3.64:1 — below AA.
             Tuned against the LIGHTEST dark surface it can land on, not the card: rows
             carry semantic highlights (warning.light is rgb(61,48,39), lighter than the
             card), and a card-tuned #747ED8 measured 3.90:1 on an error-tinted row in
             Accounting. #8E96DF gives 4.59 there and 6.18 on the card.
             Light has the same problem one surface further in: it passes at 4.70:1 on
             white, but the Leads scope control fills its active segment with the brand
             indigo at 14% and labels it with this token — 3.93:1 there. #5660BF is the
             same hue tuned against that tint (4.58) and still 5.50 on white. */
          --color-primary-text: ${mode === 'dark' ? '#8E96DF' : '#5660BF'};
          --color-divider: ${linearPalette[mode].divider};
          /* Primary left sidebar: a dark surface in both themes.
             Light used to paint primary.main (#5E6AD2) and draw white-at-alpha on it.
             White on that indigo is only 4.70:1, so the minimum alpha that clears AA is
             97% — the sidebar could not carry muted text at all, and every one of the
             designer's tiers failed: nav items /85 measured 3.89, section headings /50
             2.36, LEGAL SERVICES /60 2.75, the role label /65 2.95, and the active item
             (white on a bg-white/15 veil) 3.57. Dark mode passed on #141416, which is
             why a dark-only pass missed all of it.
             #262A54 is the same hue taken to 40% lightness. It restores headroom instead
             of flattening the hierarchy: the minimum passing alpha drops to 50%, so all
             four tiers hold as designed (4.58 / 5.60 / 6.20 / 10.26) and the active veil
             reaches 8.51. The brand indigo itself is untouched — primary.main still
             fills buttons and --color-primary-text still carries links. */
          --color-sidebar-bg: ${mode === 'dark' ? '#141416' : '#262A54'};
          --color-sidebar-border: ${mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'transparent'};
          --color-border-divider: ${linearPalette[mode].divider};
          --color-success-main: ${linearPalette[mode].success.main};
          --color-success-background: ${linearPalette[mode].success.light};
          --color-warning-main: ${linearPalette[mode].warning.main};
          --color-warning-background: ${linearPalette[mode].warning.light};
          --color-error-main: ${linearPalette[mode].error.main};
          --color-error-background: ${linearPalette[mode].error.light};
          --color-info-main: ${linearPalette[mode].info.main};
          --color-info-background: ${linearPalette[mode].info.light};

          /* Semantic colour used as TEXT. Separate from -main because -main also
             serves as a fill, and contrast is symmetric: in light mode #12B76A
             reads 2.62:1 both as green text on white AND as white text on a green
             button, so one value cannot carry both roles. The dark values already
             clear AA on navy (6.15-9.31) and are reused as-is.
             The light ones were first tuned on white — but a badge never draws them on
             white, it draws them on --color-<sem>-background, its own pastel. Measured
             on /affiliates: Suspended 3.81, Active 4.07, and the same shortfall for
             warning 3.97 and info 3.70. They are now tuned against that pastel instead,
             the same "worst surface it can land on, not the card" rule the dark values
             follow. Each keeps its hue and still clears AA on white with room to spare
             (5.17-5.67). */
          --color-success-text: ${mode === 'dark' ? '#32D583' : '#0C7D49'};
          --color-warning-text: ${mode === 'dark' ? '#FDB022' : '#A05D06'};
          --color-error-text: ${mode === 'dark' ? '#F97066' : '#CF1C0F'};
          --color-info-text: ${mode === 'dark' ? '#53B1FD' : '#1162D2'};
          
          /* Gray scale - mapped to theme mode */
          --color-gray-100: ${mode === 'dark' ? '#0F1729' : '#FCFCFD'};
          --color-gray-150: ${mode === 'dark' ? '#141C30' : '#F9FAFB'};
          --color-gray-200: ${mode === 'dark' ? '#212B47' : '#F2F4F7'};
          --color-gray-300: ${mode === 'dark' ? '#33405F' : '#E4E7EC'};
          --color-gray-400: ${mode === 'dark' ? '#4C5A7A' : '#D0D5DD'};
          --color-gray-500: ${mode === 'dark' ? '#6E7C9C' : '#98A2B3'};
          --color-gray-600: ${mode === 'dark' ? '#A7B2CC' : '#667085'};
          --color-gray-700: ${mode === 'dark' ? '#C2CBE0' : '#475467'};
          --color-gray-800: ${mode === 'dark' ? '#DDE3EF' : '#344054'};
          --color-gray-900: ${mode === 'dark' ? '#EDF0F7' : '#1D2939'};
          --color-gray-950: ${mode === 'dark' ? '#FFFFFF' : '#101828'};
          
          /* Semantic colors with light variants */
          --color-success-200: ${mode === 'dark' ? '#054F31' : '#D1FADF'};
          --color-success-600: #12B76A;
          --color-warning-200: ${mode === 'dark' ? '#7A2E0E' : '#FEF0C7'};
          --color-warning-600: #F79009;
          --color-error-100: ${mode === 'dark' ? '#7A271A' : '#FFFBFA'};
          --color-error-500: #F97066;
          --color-error-600: #F04438;
          --color-blue-100: ${mode === 'dark' ? '#194185' : '#F5FAFF'};
          --color-blue-700: #1570EF;
        }
        body {
          background-color: ${linearPalette[mode].background.default};
          color: ${linearPalette[mode].text.primary};
        }
      `,
    },
  },
});
